import React, { useEffect, useState } from "react";
import { Box, Flex, type BoxProps } from "@chakra-ui/react";
import { ChakraPath, ChakraSvg } from "ui/chakra-framer";
import { stagger, useAnimate, useInView } from "framer-motion";

const scaleFadeIn = { opacity: [0, 1], scale: [0, 1] };
const pathFadeIn = { opacity: [0, 1], pathLength: [0, 1] };
const resetScale = { opacity: [1, 0], scale: [1, 0] };
const resetPath = { opacity: [1, 0], pathLength: [1, 0] };

function useTreeAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      await animate(
        isActive
          ? [
              [".tree-node", scaleFadeIn, { delay: stagger(0.07), duration: 0.35, type: "spring" }],
              [".tree-path", pathFadeIn, { at: "-0.6", delay: stagger(0.1), duration: 0.9, ease: "easeInOut" }],
            ]
          : [
              // Reset
              [".tree-node", resetScale, { duration: 0.05, ease: "easeInOut" }],
              [".tree-path", resetPath, { at: "-0.05", duration: 0.05, ease: "easeInOut" }],
            ],
      );
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

const treePathProps = {
  className: "tree-path",
  strokeWidth: "3",
  stroke: "currentColor",
  initial: { opacity: 0, pathLength: 0 },
  fill: "none",
};

const treeNodeProps = { className: "tree-node", fill: "currentColor", initial: { opacity: 0, pathLength: 0 } };

type TreeProps = {
  isStatic?: boolean;
  color: string;
} & BoxProps;

export function TreeAnimation({ color, ...props }: TreeProps) {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useTreeAnimation(animationActive);
  const isInView = useInView(scope, { once: true, amount: 0.3 });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Flex ref={scope} color={color} {...props}>
      <Box pos="absolute" right={0} top={0} height="90px" width="100%" bgGradient="linear(to-b, white, transparent)" />

      <ChakraSvg width="100%" h="auto" fill="none" viewBox="0 0 500 575" transition="all .3s ease-in-out">
        <ChakraPath
          {...treeNodeProps}
          d="M360.046 459.293C360.046 465.157 364.8 469.911 370.665 469.911C376.529 469.911 381.283 465.157 381.283 459.293C381.283 453.428 376.529 448.674 370.665 448.674C364.8 448.674 360.046 453.428 360.046 459.293Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M301.66 563.805C301.66 569.67 306.414 574.424 312.278 574.424C318.142 574.424 322.896 569.67 322.896 563.805C322.896 557.941 318.142 553.187 312.278 553.187C306.414 553.187 301.66 557.941 301.66 563.805Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M418.431 498.216C418.431 504.081 423.185 508.835 429.05 508.835C434.914 508.835 439.668 504.081 439.668 498.216C439.668 492.352 434.914 487.598 429.05 487.598C423.185 487.598 418.431 492.352 418.431 498.216Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M478.763 376.76C478.763 382.624 483.517 387.378 489.382 387.378C495.246 387.378 500 382.624 500 376.76C500 370.895 495.246 366.141 489.382 366.141C483.517 366.141 478.763 370.895 478.763 376.76Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M139.956 498.216C139.956 504.081 135.202 508.835 129.337 508.835C123.473 508.835 118.719 504.081 118.719 498.216C118.719 492.352 123.473 487.598 129.337 487.598C135.202 487.598 139.956 492.352 139.956 498.216Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M80.9243 459.292C80.9243 465.157 76.1703 469.911 70.3059 469.911C64.4415 469.911 59.6875 465.157 59.6875 459.292C59.6875 453.428 64.4415 448.674 70.3059 448.674C76.1703 448.674 80.9243 453.428 80.9243 459.292Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M21.2368 376.76C21.2368 382.624 16.4828 387.378 10.6184 387.378C4.75402 387.378 0 382.624 0 376.76C0 370.895 4.75402 366.141 10.6184 366.141C16.4828 366.141 21.2368 370.895 21.2368 376.76Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M416.077 312.679C416.077 316.262 418.982 319.166 422.564 319.166C426.147 319.166 429.052 316.262 429.052 312.679C429.052 309.096 426.147 306.192 422.564 306.192C418.982 306.192 416.077 309.096 416.077 312.679Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M357.69 380.148C357.69 383.731 360.595 386.635 364.178 386.635C367.761 386.635 370.665 383.731 370.665 380.148C370.665 376.565 367.761 373.66 364.178 373.66C360.595 373.66 357.69 376.565 357.69 380.148Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M142.31 347.711C142.31 351.294 139.405 354.198 135.822 354.198C132.239 354.198 129.335 351.294 129.335 347.711C129.335 344.128 132.239 341.224 135.822 341.224C139.405 341.224 142.31 344.128 142.31 347.711Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M83.9228 280.243C83.9228 283.826 81.0184 286.73 77.4355 286.73C73.8527 286.73 70.9482 283.826 70.9482 280.243C70.9482 276.66 73.8527 273.756 77.4355 273.756C81.0184 273.756 83.9228 276.66 83.9228 280.243Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M386.885 245.212C386.885 248.795 389.789 251.699 393.372 251.699C396.955 251.699 399.859 248.795 399.859 245.212C399.859 241.629 396.955 238.725 393.372 238.725C389.789 238.725 386.885 241.629 386.885 245.212Z"
        />
        <ChakraPath
          {...treeNodeProps}
          d="M199.398 446.318C199.398 449.9 196.494 452.805 192.911 452.805C189.328 452.805 186.424 449.9 186.424 446.318C186.424 442.735 189.328 439.83 192.911 439.83C196.494 439.83 199.398 442.735 199.398 446.318Z"
        />
        <ChakraPath
          {...treePathProps}
          d="M250 386.489V393.068C250 403.83 260.052 412.555 270.814 412.555H349.849C362.078 412.555 371.991 422.468 371.991 434.696V459.293"
        />

        <ChakraPath
          {...treePathProps}
          d="M250 1.8V450.804C250 461.567 260.053 470.291 270.815 470.291H291.465C303.693 470.291 313.606 480.204 313.606 492.433V563.805"
        />

        <ChakraPath
          {...treePathProps}
          d="M250 319.021V325.6C250 336.362 260.05 345.087 270.812 345.087H408.233C420.461 345.087 430.375 355 430.375 367.228V498.216"
        />

        <ChakraPath
          {...treePathProps}
          d="M250 251.553V258.132C250 268.894 260.054 277.619 270.816 277.619H468.569C480.797 277.619 490.71 287.532 490.71 299.76V376.76"
        />

        <ChakraPath
          {...treePathProps}
          d="M250 354.052V360.631C250 371.393 239.949 380.118 229.186 380.118H150.151C137.923 380.118 128.01 390.031 128.01 402.259V498.216"
        />

        <ChakraPath
          {...treePathProps}
          d="M250 286.585V293.163C250 303.926 239.95 312.65 229.188 312.65H91.7675C79.5391 312.65 69.626 322.563 69.626 334.792V459.292"
        />

        <ChakraPath
          {...treePathProps}
          d="M250 219.117V225.695C250 236.458 239.947 245.182 229.185 245.182H31.4325C19.2041 245.182 9.29102 255.095 9.29102 267.324V376.76"
        />

        <ChakraPath
          {...treePathProps}
          d="M343.272 345.086H355.054C362.942 345.086 369.337 338.691 369.337 330.803V328.29C369.337 318.936 376.921 311.352 386.275 311.352H422.564"
        />

        <ChakraPath
          {...treePathProps}
          d="M284.887 412.554H296.669C304.558 412.554 310.952 406.159 310.952 398.271V395.758C310.952 386.404 318.536 378.82 327.89 378.82H364.178"
        />

        <ChakraPath
          {...treePathProps}
          d="M215.117 380.117H203.335C195.446 380.117 189.051 373.723 189.051 365.834V363.321C189.051 353.967 181.468 346.384 172.113 346.384H135.822"
        />

        <ChakraPath
          {...treePathProps}
          d="M156.73 312.65H144.948C137.059 312.65 130.665 306.255 130.665 298.366V295.854C130.665 286.499 123.081 278.916 113.727 278.916H77.4355"
        />

        <ChakraPath
          {...treePathProps}
          d="M314.081 277.619H325.863C333.752 277.619 340.147 271.224 340.147 263.335V260.823C340.147 251.468 347.73 243.885 357.085 243.885H393.372"
        />

        <ChakraPath {...treePathProps} d="M250 429.953C250 439.944 241.936 447.77 231.708 447.77H192.911" />
      </ChakraSvg>
    </Flex>
  );
}
